import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useRequest } from "~/v2/api/hooks";
import { type AtdOrdersListKeyParams, queryKeys } from "~/v2/api/queryKeys";
import type { Order } from "~/v2/api/rest/account";
import { type GetAtdOrdersParams, getAtdOrders } from "./api";
import type { OrdersWidgetTimeWindow } from "./OrdersWidget.types";
import {
  getRollingOrdersAfter,
  isOrdersRollingTimeWindow,
} from "./ordersTimeWindow";

export const ATD_ORDERS_TIME_WINDOW_AFTER_KEYS = {
  all: "all",
  last24Hours: "rolling-24-hours",
  last3Days: "rolling-3-days",
  last7Days: "rolling-7-days",
  last1Month: "rolling-1-month",
} as const;

const EMPTY_CURSOR_HISTORY: string[] = [];
const EMPTY_ORDERS: Order[] = [];

export const MAX_ATD_ORDERS_PAGE_SIZE = 499;

export type UseAtdOrdersParams = Omit<
  GetAtdOrdersParams,
  "accountID" | "product" | "afterOrderID" | "beforeOrderID" | "limit"
> & {
  accountID?: GetAtdOrdersParams["accountID"];
  product?: GetAtdOrdersParams["product"];
  pageSize: number;
  timeWindow?: OrdersWidgetTimeWindow;
  enabled?: boolean;
  pollingInterval?: number | false;
};

interface PaginationState {
  filterSignature: string;
  cursors: string[];
}

const resolveTimeWindowAfterKey = (
  timeWindow: OrdersWidgetTimeWindow,
  after: string | undefined,
): string => {
  if (isOrdersRollingTimeWindow(timeWindow)) {
    return ATD_ORDERS_TIME_WINDOW_AFTER_KEYS[timeWindow];
  }
  if (timeWindow === "custom") {
    return after ?? "custom";
  }
  return ATD_ORDERS_TIME_WINDOW_AFTER_KEYS.all;
};

const resolveRequestTimeBounds = (
  timeWindow: OrdersWidgetTimeWindow,
  after: string | undefined,
  until: string | undefined,
): { after: string | undefined; until: string | undefined } => {
  if (isOrdersRollingTimeWindow(timeWindow)) {
    return {
      after: getRollingOrdersAfter(timeWindow),
      until: undefined,
    };
  }
  if (timeWindow === "custom") {
    return { after, until };
  }
  return { after: undefined, until: undefined };
};

const normalizePageSize = (pageSize: number): number => {
  if (!Number.isFinite(pageSize)) {
    return 1;
  }
  return Math.min(MAX_ATD_ORDERS_PAGE_SIZE, Math.max(1, Math.floor(pageSize)));
};

const normalizeSymbols = (symbols?: string[]): string[] =>
  (symbols ?? []).filter(Boolean).sort();

export const getAtdOrdersPage = (
  orders: Order[] | undefined,
  pageSize: number,
): { orders: Order[]; hasNextPage: boolean } => {
  if (!orders) {
    return { orders: EMPTY_ORDERS, hasNextPage: false };
  }

  return {
    orders: orders.slice(0, pageSize),
    hasNextPage: orders.length > pageSize,
  };
};

export const useAtdOrders = ({
  accountID,
  product,
  pageSize: requestedPageSize,
  symbols,
  status,
  side,
  after,
  until,
  assetClass,
  orderType,
  direction = "desc",
  timeWindow = "all",
  enabled = true,
  pollingInterval = false,
}: UseAtdOrdersParams) => {
  const pageSize = normalizePageSize(requestedPageSize);
  const requestLimit = pageSize + 1;
  const afterKey = resolveTimeWindowAfterKey(timeWindow, after);
  const requestUntil = timeWindow === "custom" ? until : undefined;
  const normalizedSymbols = useMemo(() => normalizeSymbols(symbols), [symbols]);

  const baseKeyParams: Omit<
    AtdOrdersListKeyParams,
    "afterOrderID" | "beforeOrderID"
  > = {
    // Empty identity values only represent the disabled query key. The query
    // function below guards both values before it can choose an endpoint.
    accountID: accountID ?? "",
    product: product ?? "live",
    symbols: normalizedSymbols,
    status,
    side,
    after: afterKey,
    until: requestUntil,
    assetClass,
    orderType,
    direction,
    pageSize,
    requestLimit,
  };
  const filterSignature = JSON.stringify(baseKeyParams);
  const [pagination, setPagination] = useState<PaginationState>({
    filterSignature,
    cursors: [],
  });
  const navigationInFlightRef = useRef(false);
  const [isPageTransitioning, setIsPageTransitioning] = useState(false);

  const cursorHistory =
    pagination.filterSignature === filterSignature
      ? pagination.cursors
      : EMPTY_CURSOR_HISTORY;
  const currentCursor = cursorHistory.at(-1);
  const cursorKeyParams: Pick<
    AtdOrdersListKeyParams,
    "afterOrderID" | "beforeOrderID"
  > =
    direction === "asc"
      ? { afterOrderID: currentCursor }
      : { beforeOrderID: currentCursor };
  const keyParams: AtdOrdersListKeyParams = {
    ...baseKeyParams,
    ...cursorKeyParams,
  };

  useEffect(() => {
    setPagination((current) =>
      current.filterSignature === filterSignature
        ? current
        : { filterSignature, cursors: [] },
    );
  }, [filterSignature]);

  const requestParams = {
    symbols: normalizedSymbols,
    status,
    side,
    limit: requestLimit,
    until: requestUntil,
    assetClass,
    orderType,
    direction,
  };

  const {
    data,
    dataUpdatedAt,
    error,
    isError,
    isFetching,
    isLoading,
    isRefetchError,
    refetch,
  } = useRequest(
    queryKeys.orders.atdList(keyParams),
    () => {
      if (!accountID || !product) {
        return Promise.resolve(EMPTY_ORDERS);
      }

      const { after: effectiveAfter, until: effectiveUntil } =
        resolveRequestTimeBounds(timeWindow, after, until);

      return direction === "asc"
        ? getAtdOrders({
            ...requestParams,
            accountID,
            product,
            after: effectiveAfter,
            until: effectiveUntil,
            afterOrderID: currentCursor,
          })
        : getAtdOrders({
            ...requestParams,
            accountID,
            product,
            after: effectiveAfter,
            until: effectiveUntil,
            beforeOrderID: currentCursor,
          });
    },
    {
      enabled: enabled && Boolean(accountID && product),
      refetchInterval: pollingInterval,
      // This widget owns visible first-load and refresh error recovery.
      meta: { showErrorToast: false },
    },
  );

  const page = getAtdOrdersPage(data, pageSize);
  const nextCursor = page.orders.at(-1)?.id;
  const hasNextPage = page.hasNextPage && Boolean(nextCursor);
  const hasPreviousPage = cursorHistory.length > 0;
  const [accumulatedOrders, setAccumulatedOrders] =
    useState<Order[]>(EMPTY_ORDERS);
  const cursorCountRef = useRef(0);

  useEffect(() => {
    const pageOrders = getAtdOrdersPage(data, pageSize).orders;
    const cursorCount = cursorHistory.length;
    setAccumulatedOrders((current) => {
      if (cursorCount === 0) {
        return pageOrders;
      }
      if (cursorCount < cursorCountRef.current) {
        return pageOrders;
      }
      const seen = new Set(current.map((order) => order.id));
      const appended = pageOrders.filter((order) => !seen.has(order.id));
      return appended.length === 0 ? current : [...current, ...appended];
    });
    cursorCountRef.current = cursorCount;
  }, [cursorHistory.length, data, pageSize]);

  useEffect(() => {
    if (isPageTransitioning && !isFetching) {
      navigationInFlightRef.current = false;
      setIsPageTransitioning(false);
    }
  }, [isFetching, isPageTransitioning]);

  const nextPage = useCallback(() => {
    if (!hasNextPage || !nextCursor || navigationInFlightRef.current) {
      return;
    }

    navigationInFlightRef.current = true;
    setIsPageTransitioning(true);
    setPagination((current) => {
      const currentCursors =
        current.filterSignature === filterSignature ? current.cursors : [];
      if (currentCursors.at(-1) === nextCursor) {
        return current;
      }
      return {
        filterSignature,
        cursors: [...currentCursors, nextCursor],
      };
    });
  }, [filterSignature, hasNextPage, nextCursor]);

  const previousPage = useCallback(() => {
    if (!hasPreviousPage || navigationInFlightRef.current) {
      return;
    }

    navigationInFlightRef.current = true;
    setIsPageTransitioning(true);
    setPagination((current) => {
      if (
        current.filterSignature !== filterSignature ||
        current.cursors.length === 0
      ) {
        return current;
      }
      return {
        filterSignature,
        cursors: current.cursors.slice(0, -1),
      };
    });
  }, [filterSignature, hasPreviousPage]);

  return {
    orders: accumulatedOrders,
    error,
    isError,
    isFetching,
    isLoading: isLoading && accumulatedOrders.length === 0,
    isPageTransitioning,
    isFetchingNextPage: isPageTransitioning && accumulatedOrders.length > 0,
    isRefetchError,
    dataUpdatedAt,
    hasNextPage,
    hasPreviousPage,
    pageNumber: cursorHistory.length + 1,
    nextPage,
    previousPage,
    refetch,
  };
};
